import { browser } from '$app/environment';
import type { GetCartQuery } from '$gql/storefront/operations';
import type { CurrencyCode } from '$gql/storefront/schemas';
import { derived } from 'svelte/store';
import { trimId } from './utils/shopify/productsId';
import { page } from '$app/stores';
import { GUEST, LOGGED_IN } from './constants';
import type { ABTestVersion } from './utils/ab-test.server';

type TrackableProduct = {
  item_id: string;
  item_name: string;
  item_category: string;
};

export enum AnalyticsEvents {
  SHOP_LOAD_MORE = 'shop_load_more',
  ADD_TO_CART = 'add_to_cart',
  REMOVE_FROM_CART = 'remove_from_cart',
  BEGIN_CHECKOUT = 'begin_checkout',
  VIEW_CART = 'view_cart',
  VIEW_PRODUCT_IMAGE = 'view_product_image',
  IMAGE_DESCRIPTION_MODULE = 'image_description_module',
  OPEN_STYLED_BY_YOU = 'open_styled_by_you',
  OPEN_REVIEWS = 'open_reviews',
  OPEN_PRODUCT_DETAIL = 'open_product_detail',
  VIEW_ITEM = 'view_item',
  GAME_OVER = 'game_over',
  ADD_TO_WISHLIST = 'add_to_wishlist',
  REMOVE_FROM_WISHLIST = 'remove_from_wishlist',
  COOKIES_CONSENT = 'cookies_consent',
  PAGE_VIEW = 'page_view'
}

export enum ElevarAnalyticsEvents {
  VIEW_ITEM = 'dl_view_item',
  ADD_TO_CART = 'dl_add_to_cart',
  REMOVE_FROM_CART = 'dl_remove_from_cart',
  VIEW_CART = 'dl_view_cart',
  USER_DATA = 'dl_user_data',
  SUBSCRIBE = 'dl_subscribe'
}

export enum AddToCartLocations {
  PDP_BUTTON = 'PDP main button',
  SHOP_THE_LOOK = 'Shop the look',
  CART_RECOMMENDATION = 'Cart recommendation',
  CART_GIFT_BOX = 'Cart gift box product',
  CART_QUANTITY_SELECTOR = 'Cart quantity selector',
  STYLED_BY_YOU = 'Styled by you block',
  WISHLIST = 'Wishlist',
  ORDERS = 'Orders',
  PRODUCT_CARD = 'Product card'
}

export enum FacebookAnalyticsEvents {
  VIEW_CONTENT = 'ViewContent',
  ADD_TO_CART = 'AddToCart',
  BEGIN_CHECKOUT = 'InitiateCheckout'
}

export enum KlavyioAnalyticsEvents {
  VIEWED_PRODUCT = 'Viewed Product',
  ADDED_TO_CART = 'Added to Cart'
}

type AnalyticsEvent =
  | {
      event: AnalyticsEvents.SHOP_LOAD_MORE;
      collection_name: string;
      nth_session_click: number;
    }
  | {
      event: AnalyticsEvents.REMOVE_FROM_CART | AnalyticsEvents.VIEW_CART;
      value: number;
      currency: CurrencyCode;
      items: (TrackableProduct & { item_variant: string | null | undefined })[];
    }
  | {
      event: AnalyticsEvents.ADD_TO_CART;
      value: number;
      currency: CurrencyCode;
      items: (TrackableProduct & {
        item_variant: string | null | undefined;
        item_sku: string | null | undefined;
      })[];
      item_list_name: AddToCartLocations | undefined;
      test_group?: ABTestVersion;
    }
  | {
      event:
        | AnalyticsEvents.ADD_TO_WISHLIST
        | AnalyticsEvents.REMOVE_FROM_WISHLIST
        | AnalyticsEvents.BEGIN_CHECKOUT;
      value: number;
      currency: CurrencyCode;
      items: (TrackableProduct & {
        item_variant: string | null | undefined;
        item_sku: string | null | undefined;
      })[];
    }
  | {
      event: AnalyticsEvents.VIEW_PRODUCT_IMAGE;
      number: number;
      collection_name: string;
      items: TrackableProduct[];
    }
  | {
      event: AnalyticsEvents.IMAGE_DESCRIPTION_MODULE;
      title: string;
      items: (TrackableProduct & { item_variant: string | null | undefined })[];
    }
  | {
      event: AnalyticsEvents.OPEN_STYLED_BY_YOU;
      username: string;
      date?: string;
    }
  | {
      event: AnalyticsEvents.OPEN_REVIEWS;
      items: (TrackableProduct & { item_variant: string | null | undefined })[];
    }
  | {
      event: AnalyticsEvents.VIEW_ITEM;
      items: (TrackableProduct & {
        item_variant: string | null | undefined;
        item_sku: string | null | undefined;
      })[];
      test_group?: ABTestVersion;
    }
  | {
      event: AnalyticsEvents.OPEN_PRODUCT_DETAIL;
      title: string;
      items: (TrackableProduct & { item_variant: string | null | undefined })[];
    }
  | {
      event: AnalyticsEvents.GAME_OVER;
      score: number;
      best_score: number;
    }
  | {
      event: AnalyticsEvents.COOKIES_CONSENT;
      consent_functional: boolean;
      consent_analytical: boolean;
      consent_marketing: boolean;
    }
  | {
      event: AnalyticsEvents.PAGE_VIEW;
      test_group: ABTestVersion;
    };

type FacebookAnalyticsEvent =
  | {
      event: FacebookAnalyticsEvents.ADD_TO_CART;
      value: number;
      currency: CurrencyCode;
      content_ids: [string];
      content_type: 'product_group';
      content_category: string;
      content_name: string;
      num_items: 1;
    }
  | {
      event: FacebookAnalyticsEvents.VIEW_CONTENT;
      value: number;
      currency: CurrencyCode;
      content_ids: [string];
      content_type: 'product_group';
      content_category: string;
      content_name: string;
    }
  | {
      event: FacebookAnalyticsEvents.BEGIN_CHECKOUT;
      value: number;
      currency: CurrencyCode;
      content_ids: string[];
      content_type: string;
      num_items: number;
      contents: { id: string; quantity: number }[];
    };

type KlavyioAnalyticsEvent =
  | {
      event: KlavyioAnalyticsEvents.VIEWED_PRODUCT;
      Name: string;
      ProductID: string;
      Categories: string[];
      ImageURL: string;
      URL: string;
      Brand: string;
      Price: number;
      CompareAtPrice: number | null;
    }
  | {
      event: KlavyioAnalyticsEvents.ADDED_TO_CART;
      cart: {
        total_price: string;
        $value: string;
        original_total_price: string;
        items: unknown[];
      };
    };

export type ElevarProduct = {
  id?: string | null; // SKU
  name: string; // Product title
  brand: string;
  category: string;
  variant: string;
  price: string;
  quantity?: string; // Not required for dl_select_item & dl_view_item
  position?: number; // Only required for dl_select_item; position in the list of search results, collection views and position in cart indexed starting at 1
  list: string; // The list the product was discovered from i.e. the collection url
  product_id: string; // The product_id
  variant_id: string; // id or variant_id
  compare_at_price: string; // If available on dl_view_item & dl_add_to_cart otherwise use "0.0"
  image?: string; // If available, otherwise use an empty string
  url?: string; // URL for the Product Page. Only required for dl_add_to_cart.
};

export type ElevarImpression = {
  id: string | null; // SKU
  name: string; // Product title
  brand: string;
  category: string;
  variant: string;
  price: string;
  quantity: string;
  product_id: string; // The product_id
  variant_id: string; // id or variant_id
  compare_at_price: string; // If available on dl_view_item & dl_add_to_cart otherwise use "0.0"
  image?: string; // If available, otherwise use an empty string
  url?: string; // URL for the Product Page. Only required for dl_add_to_cart.
  position?: number; // position in the list of search results, collection views and position in cart indexed starting at 1
};

type ElevaUser = {
  visitor_type: typeof LOGGED_IN | typeof GUEST;
};

type ElevarAnalyticsEvent =
  | {
      event: ElevarAnalyticsEvents.USER_DATA;
      cart_total: string;
      user_properties: ElevaUser;
      ecommerce: {
        currencyCode: string;
        cart_contents: {
          products: ElevarProduct[];
        };
      };
    }
  | {
      event: ElevarAnalyticsEvents.VIEW_ITEM;
      user_properties: ElevaUser;
      ecommerce: {
        currencyCode: string;
        detail: {
          actionField: {
            list: string;
            action: string;
          };
          products: ElevarProduct[];
        };
      };
    }
  | {
      event: ElevarAnalyticsEvents.ADD_TO_CART;
      user_properties: ElevaUser;
      ecommerce: {
        currencyCode: string;
        add: {
          actionField: {
            list: string;
          };
          products: ElevarProduct[];
        };
      };
    }
  | {
      event: ElevarAnalyticsEvents.REMOVE_FROM_CART;
      user_properties: ElevaUser;
      ecommerce: {
        currencyCode: string;
        remove: {
          actionField: {
            list: string;
          };
          products: ElevarProduct[];
        };
      };
    }
  | {
      event: ElevarAnalyticsEvents.VIEW_CART;
      user_properties: ElevaUser;
      cart_total: string;
      ecommerce: {
        currencyCode: string;
        actionField: {
          list: string;
        };
        impressions: ElevarImpression[];
      };
    }
  | {
      event: ElevarAnalyticsEvents.SUBSCRIBE;
      lead_type: 'email';
      user_properties: {
        customer_email: string;
      };
    };

const KLAVYIO_IDENTIFY_KEY = 'identify' as const;
const KLAVYIO_VIEWED_ITEM_KEY = 'trackViewedItem' as const;

export const analyticsEvent = (e: AnalyticsEvent) => {
  if (!browser || !window?.dataLayer) return;
  const { event, ...rest } = e;

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ecommerce: { ...rest } });
};

export const elevarAnalyticsEvent = (e: ElevarAnalyticsEvent) => {
  if (!browser || !window?.ElevarDataLayer) return;
  const { event, ...rest } = e;

  window.ElevarDataLayer.push({ event, ...rest });
};

export const facebookAnalyticsEvent = (e: FacebookAnalyticsEvent) => {
  if (!browser || !window?.fbq) return;
  const { event, ...rest } = e;

  window.fbq('track', event, { ...rest });
};

export const klavyioAnalyticsEvent = (e: KlavyioAnalyticsEvent) => {
  if (!browser || !window?._learnq) return;
  const { event, ...rest } = e;

  window._learnq.push(['track', event, rest]);
};

export const klavyioIdentify = (user: {
  $email: string;
  $first_name?: string | null;
  $last_name?: string | null;
}) => {
  if (!browser || !window?._learnq) return;
  window._learnq.push([KLAVYIO_IDENTIFY_KEY, user]);
};

export const klavyioViewedItem = (item: {
  Title: string;
  ItemId: string;
  Categories: string[];
  ImageUrl: string;
  URL: string;
  Metadata: {
    Brand: string;
    Price: number;
    CompareAtPrice: number | null;
  };
}) => {
  if (!browser || !window?._learnq) return;
  window._learnq.push([KLAVYIO_VIEWED_ITEM_KEY, item]);

  klavyioAnalyticsEvent({
    event: KlavyioAnalyticsEvents.VIEWED_PRODUCT,
    ProductID: item.ItemId,
    Name: item.Title,
    URL: item.URL,
    ImageURL: item.ImageUrl,
    Price: item.Metadata.Price,
    CompareAtPrice: item.Metadata.CompareAtPrice,
    Brand: item.Metadata.Brand,
    Categories: item.Categories
  });
};

export const parseCartToAnalyticsEvent = (cart: NonNullable<GetCartQuery['cart']>) => {
  return {
    currency: cart.cost.totalAmount.currencyCode,
    value: +cart.cost.totalAmount.amount,
    items: cart.lines.nodes.map((line) => {
      const variantTitle = line.merchandise.selectedOptions
        .map((option) => option.value)
        .join(' / ');
      return {
        item_id: line.merchandise.product.handle,
        item_name: line.merchandise.product.title,
        item_category: line.merchandise.product.productType,
        item_variant: variantTitle,
        item_sku: line.merchandise.sku
      };
    })
  };
};

export const parseCartToFacebookAnalyticsEvent = (cart: NonNullable<GetCartQuery['cart']>) => {
  return {
    value: +cart.cost.totalAmount.amount,
    currency: cart.cost.totalAmount.currencyCode,
    num_items: cart.totalQuantity,
    content_type: 'product_group',
    content_ids: cart.lines.nodes.map((line) => trimId(line.merchandise.id)),
    contents: cart.lines.nodes.map((line) => ({
      id: trimId(line.merchandise.product.id),
      quantity: line.quantity
    }))
  };
};

export enum UrlTrackers {
  HOMEPAGE = '?Homepage',
  NAVIGATION = '?Navigation',
  FOOTER = '?Footer',
  GROUP = '?Group'
}

export const autoTracker = derived([page], ([$page]) => {
  const [, , ...rest] = $page.url.pathname.split('/').filter(Boolean);

  // add tracker to links coming from homepage
  if (!rest.length) {
    return UrlTrackers.HOMEPAGE;
  }

  return undefined;
});
